<script lang="ts">
  import { options, screens } from '../../store';
  import { memory } from '../utils/memory';
  import { optionData } from './modals';
  import * as Sentry from '@sentry/svelte';
  import { prompt } from './modals';
  import Frame from '../components/frame.svelte';
  import Chrome from '../components/chrome.svelte';
  import { Screen } from '../models/screen.type';

  type Toggle = 'tooltipsKeywords' | 'groupTechniques' | 'autoPlay' | 'invincible';
  type Volume = 'volumeEffects' | 'volumeMusic';

  let onConfirm: Function | null = null;

  optionData.subscribe((value) => {
    if (value && value.callback) {
      onConfirm = value.callback;
    } else {
      onConfirm = null;
    }
  });

  const confirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    optionData.set(null);
  };

  options.subscribe((value) => {
    type OptionKeys = keyof typeof value;
    for (let key of Object.keys(value) as OptionKeys[]) {
      memory.persist(key, value[key]);
    }
  });

  async function reportBug() {
    confirm();
    var text = await prompt({
      title: 'Report Bug',
      text: 'Please describe the bug you encountered.',
    });

    if (text) {
      Sentry.captureMessage(text);
    }
  }

  const openStyles = () => {
    screens.set(Screen.Styles);
    confirm();
  };
</script>

{#snippet toggle(label: string, hint: string, key: Toggle)}
  <label class="row">
    <span class="text">
      <span class="label">{label}</span>
      <span class="hint">{hint}</span>
    </span>
    <input type="checkbox" class="toggle" bind:checked={$options[key]} />
  </label>
{/snippet}

{#snippet volume(label: string, key: Volume)}
  <label class="row">
    <span class="text">
      <span class="label">{label}</span>
    </span>
    <span class="volume">
      <input
        type="range"
        min="0"
        max="100"
        step="1"
        style="--fill: {$options[key]}%"
        bind:value={$options[key]}
      />
      <span class="value">{$options[key]}</span>
    </span>
  </label>
{/snippet}

{#if $optionData}
  <div class="modal-container">
    <div class="modal options">
      <Frame>
        <div class="header">
          <h1>Options</h1>
        </div>

        <div class="content">
          <section>
            <h2>Gameplay</h2>
            {@render toggle(
              'Keyword tooltips',
              'Explain keywords when you hover them',
              'tooltipsKeywords',
            )}
            {@render toggle(
              'Group techniques',
              'Sort techniques by kind in battle',
              'groupTechniques',
            )}
          </section>

          <section>
            <h2>Sound</h2>
            {@render volume('Effects', 'volumeEffects')}
            {@render volume('Music', 'volumeMusic')}
          </section>

          <section>
            <h2>Testing</h2>
            {@render toggle('Auto-play', 'Let the AI take your turns', 'autoPlay')}
            {@render toggle('Invincible', 'Take no damage', 'invincible')}
          </section>
        </div>

        <div class="functions">
          <Chrome chroming="obsidian" edge="circle" thickness="thin" click={reportBug}>
            <div class="button">Report Bug</div>
          </Chrome>
          <Chrome chroming="obsidian" edge="circle" thickness="thin" click={openStyles}>
            <div class="button">View Styles</div>
          </Chrome>
          <span class="spacer"></span>
          <Chrome chroming="gold" edge="circle" thickness="thin" click={confirm}>
            <div class="button">Close</div>
          </Chrome>
        </div>
      </Frame>
    </div>
  </div>
{/if}

<style lang="less">
  @import '../../defines.less';

  // Brass for anything switched on or filled, steel for the parts you grab --
  // the same two metals as the frame's border and its corner clamps.
  @brass: linear-gradient(to bottom, @edge-shine 0%, @yellow 30%, @edge-warm 100%);
  @steel: radial-gradient(circle at 35% 30%, #d4d4b6 0%, #888880 45%, #3b3b37 100%);

  .options {
    // Fill the frame, rather than shrinking to fit the widest label.
    width: min(600px, 90vw);

    // Denser than the frame's usual fill: the menu buttons behind glowed
    // through and fought with the rows.
    :global(.frame) {
      background: radial-gradient(fade(@panel, 88%), fade(@panel-sunken, 96%));
    }
  }

  .header h1 {
    width: 100%;
    text-align: center;
    color: @edge-shine;
    letter-spacing: 0.06em;
  }

  .content {
    display: grid;
    grid-gap: 22px;
    text-align: left;

    @media @small {
      grid-gap: 14px;
    }
  }

  h2 {
    margin: 0 0 4px;
    font-size: 0.8em;
    font-weight: 400;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: @edge-shine;
    opacity: 0.7;

    &::after {
      content: '';
      display: block;
      margin-top: 6px;
      .glossary-rule();
    }
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    grid-gap: 16px;
    padding: 8px 6px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 150ms linear;

    &:hover {
      background: fade(@edge-shine, 6%);
    }
  }

  .text {
    display: flex;
    flex-direction: column;
    grid-gap: 2px;
    min-width: 0;
  }

  .label {
    font-size: 1.1em;
  }

  .hint {
    font-size: 0.75em;
    opacity: 0.55;
  }

  // --- Toggle ------------------------------------------------------------
  // A slot sunk into the panel with a steel stud that slides along it; the
  // slot fills with brass when on.
  .toggle {
    appearance: none;
    -webkit-appearance: none;
    flex: 0 0 auto;
    position: relative;
    width: 52px;
    height: 26px;
    margin: 0;
    border-radius: 13px;
    background: @panel-sunken;
    border: 2px solid @black;
    box-shadow:
      inset 0 0 0 1px @panel-line,
      inset 0 2px 6px fade(@black, 80%);
    cursor: pointer;

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: @steel;
      box-shadow:
        0 0 0 1.5px fade(@black, 50%),
        0 1px 3px fade(@black, 70%);
      transition: transform 200ms ease;
    }

    &:checked {
      background: @brass;
      box-shadow:
        inset 0 0 0 1px @edge-mid,
        inset 0 2px 5px fade(@edge-deep, 70%),
        0 0 8px fade(@yellow, 35%);

      &::before {
        transform: translateX(26px);
      }
    }

    &:focus-visible {
      outline: 2px solid @edge-shine;
      outline-offset: 2px;
    }
  }

  // --- Volume ------------------------------------------------------------
  .volume {
    display: flex;
    align-items: center;
    grid-gap: 12px;
    flex: 0 1 260px;
  }

  .value {
    width: 2.2em;
    text-align: right;
    font-variant-numeric: tabular-nums;
    opacity: 0.8;
  }

  // The track is drawn on the input itself so the brass can stop at the
  // thumb: --fill is the value as a percentage.
  input[type='range'] {
    appearance: none;
    -webkit-appearance: none;
    flex: 1 1 auto;
    min-width: 0;
    height: 10px;
    margin: 0;
    border-radius: 5px;
    border: 2px solid @black;
    background:
      @brass 0 0 / var(--fill) 100% no-repeat,
      @panel-sunken;
    box-shadow: inset 0 0 0 1px @panel-line;
    cursor: pointer;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: @steel;
      border: none;
      box-shadow:
        0 0 0 1.5px fade(@black, 60%),
        0 1px 3px fade(@black, 70%);
    }

    &::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: @steel;
      border: none;
      box-shadow:
        0 0 0 1.5px fade(@black, 60%),
        0 1px 3px fade(@black, 70%);
    }

    &:focus-visible {
      outline: 2px solid @edge-shine;
      outline-offset: 4px;
    }
  }

  // --- Buttons -----------------------------------------------------------
  // Report and Styles on the left, Close on its own at the right; on a phone
  // the spacer takes a whole line, so Close drops beneath the other two.
  .functions {
    flex-wrap: wrap;
    align-items: center;

    .spacer {
      flex: 1 1 auto;
    }

    .button {
      white-space: nowrap;
    }

    @media @mobile {
      .spacer {
        flex-basis: 100%;
        height: 0;
      }
    }
  }
</style>
